import { useEffect, useMemo, useState } from "react";
import {
  reinforcedConcreteResourceGroups,
  reinforcedConcreteWorkUnits,
  type ParametricField,
} from "../domain/reinforcedConcrete";
import { deriveReinforcedConcreteQuantities } from "../domain/reinforcedConcreteEngine";
import { supabase } from "../lib/supabase";
import { costResourceQuantities } from "../domain/resourceCosting";
import { priceOriginLabel } from "../domain/priceOrigin.ts";
import PilotPricePanel from "./PilotPricePanel";
import { priceResolutionSnapshot, type PilotPriceResolution } from "../domain/pilotConcreteResolution.ts";
import ArticleBasePicker, { type ArticleBase } from "./ArticleBasePicker";
import SaveParametricDraft, { type EngineEstimateTarget } from "./SaveParametricDraft";
import HistoricalYieldReferences from "./HistoricalYieldReferences";
import type { HistoricalYieldSelection } from "../domain/historicalYieldSelection";

type ValueMap = Record<string, string>;

export type ParametricEngineDraft = {
  workCode: string;
  values: ValueMap;
  historicalYield: HistoricalYieldSelection | null;
  articleBase?: ArticleBase | null;
};

type ResourcePriceRow = {
  resource_id: string;
  resource_price_id: string | null;
  source_document_id: string | null;
  code: string;
  name: string;
  unit: string;
  unit_price: number | null;
  effective_date: string | null;
  counterparty: string | null;
};

const familyLabels = {
  foundation: "Fundações",
  vertical: "Elementos verticais",
  horizontal: "Elementos horizontais",
  other: "Outros",
} as const;

function FieldInput({
  field,
  value,
  onChange,
}: {
  field: ParametricField;
  value: string;
  onChange: (value: string) => void;
}) {
  if (field.type === "choice" || field.type === "boolean") {
    const options = field.type === "boolean"
      ? [{ value: "true", label: "Sim" }, { value: "false", label: "Não" }]
      : field.options ?? [];
    return <div className="engine-choice-buttons" role="group" aria-label={field.label}>
      {options.map((option) => <button key={option.value} type="button"
        className={value === option.value ? "button button-primary" : "button button-secondary"}
        aria-pressed={value === option.value} onClick={() => onChange(option.value)}>
        {option.label}
      </button>)}
    </div>;
  }

  return (
    <input
      inputMode="decimal"
      min={field.min}
      max={field.max}
      placeholder={field.unit ? "Valor em " + field.unit : "Valor"}
      step="any"
      type="number"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export default function ParametricPriceEngine({ canUseHistoricalYield, onReviewYields, draft, onDraftChange, target, onReturn }: {
  target: EngineEstimateTarget | null;
  onReturn: () => void;
  draft: ParametricEngineDraft | null;
  onDraftChange: (draft: ParametricEngineDraft) => void;
  canUseHistoricalYield: boolean;
  onReviewYields: () => void;
}) {
  const [selectedCode, setSelectedCode] = useState(draft?.workCode ?? "BA.VERT.PILAR");
  const [values, setValues] = useState<ValueMap>(draft?.values ?? {});
  const [selectedYield, setSelectedYield] = useState<HistoricalYieldSelection | null>(draft?.historicalYield ?? null);
  const [articleBase, setArticleBase] = useState<ArticleBase | null>(draft?.articleBase ?? null);
  const [resourcePrices, setResourcePrices] = useState<Record<string, ResourcePriceRow>>({});
  const [pricesReady, setPricesReady] = useState(false);
  const [resourcePriceError, setResourcePriceError] = useState("");
  const [pilotResolution, setPilotResolution] = useState<PilotPriceResolution | null>(null);

  // Keep the draft in the signed-in app session while review/resources tabs are open.
  useEffect(() => {
    onDraftChange({ workCode: selectedCode, values, historicalYield: selectedYield, articleBase });
  }, [selectedCode, values, selectedYield, articleBase, onDraftChange]);

  const selected = useMemo(
    () => reinforcedConcreteWorkUnits.find((item) => item.code === selectedCode) ?? null,
    [selectedCode],
  );

  useEffect(() => {
    let cancelled = false;

    void supabase
      .from("current_resource_prices")
      .select("resource_id,resource_price_id,source_document_id,code,name,unit,unit_price,effective_date,counterparty")
      .then(({ data, error }) => {
        if (cancelled) return;

        if (error) {
          setResourcePriceError(error.message);
          setResourcePrices({});
          return;
        }

        const next: Record<string, ResourcePriceRow> = {};
        for (const row of (data ?? []) as ResourcePriceRow[]) {
          next[row.code] = row;
        }
        setResourcePrices(next);
        setResourcePriceError("");
        setPricesReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  function choose(code: string) {
    setSelectedCode(code);
    setValues({});
    setSelectedYield(null);
    setArticleBase(null);
    setPilotResolution(null);
  }

  const filledRequired = selected
    ? selected.fields.filter((field) => field.required && values[field.key]?.trim()).length
    : 0;
  const required = selected?.fields.filter((field) => field.required).length ?? 0;
  const derivedQuantities = selected
    ? deriveReinforcedConcreteQuantities(selected.code, values)
    : [];

  const {
    lines: pricedLines,
    subtotal: calculableSubtotal,
    missingPriceCount,
    pricedLineCount,
  } = costResourceQuantities(derivedQuantities, resourcePrices);

  function formatMoney(value: number | null) {
    if (value === null) return "—";
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(value);
  }

  return (
    <section className="stack-xl">
      <header className="page-header">
        <div>
          <p className="eyebrow">Gerador de Preços L37 · V2</p>
          <h2>Motor paramétrico</h2>
          <p className="muted">
            Configure a unidade e consulte a composição técnica disponível.
          </p>
        </div>
        <div className="engine-status">
          <span>Fundação técnica</span>
          <strong>Betão armado</strong>
        </div>
      </header>

      <div className="engine-layout">
        <aside className="engine-catalog card">
          <div className="engine-catalog-head">
            <strong>Unidades de obra</strong>
            <span>{reinforcedConcreteWorkUnits.length}</span>
          </div>

          {(Object.keys(familyLabels) as Array<keyof typeof familyLabels>).map((family) => {
            const items = reinforcedConcreteWorkUnits.filter((item) => item.family === family);
            return (
              <div className="engine-family" key={family}>
                <p>{familyLabels[family]}</p>
                {items.map((item) => (
                  <button
                    className={item.code === selectedCode ? "engine-unit active" : "engine-unit"}
                    key={item.code}
                    type="button"
                    onClick={() => choose(item.code)}
                  >
                    <span>{item.name}</span>
                    <small>{item.unit}</small>
                  </button>
                ))}
              </div>
            );
          })}
        </aside>

        <main className="engine-work">
          {selected ? (
            <>
              <section className="engine-title card">
                <div>
                  <div className="result-meta">
                    <span className="pill">{selected.unit}</span>
                    <span className="mono">{selected.code}</span>
                    <span>{familyLabels[selected.family]}</span>
                  </div>
                  <h3>{selected.name}</h3>
                  <p className="muted">{selected.description}</p>
                </div>
                <div className="engine-completion">
                  <span>Campos obrigatórios preenchidos</span>
                  <strong>
                    {filledRequired}/{required}
                  </strong>
                </div>
              </section>

              <ArticleBasePicker key={selected.code} workCode={selected.code} onApply={(base) => {
                setValues(Object.fromEntries(selected.fields
                  .filter((field) => Object.hasOwn(base.parameter_values, field.key))
                  .map((field) => [field.key, String(base.parameter_values[field.key])])));
                setSelectedYield(null); setArticleBase(base);
              }} />
              {articleBase ? <div className="alert alert-success">
                Base carregada: {articleBase.name}. Os parâmetros abaixo podem ser adaptados.
                {articleBase.review_status === "pending" ? " Referência ainda por rever pela L37." : ""}
                <p>Os preços da tabela de origem são indicativos; o cálculo abaixo usa os preços L37 disponíveis.</p>
              </div> : null}

              <section className="card engine-parameters">
                <div className="engine-section-head">
                  <div>
                    <p className="eyebrow">1. Parâmetros técnicos</p>
                    <h3>Caracterizar a unidade</h3>
                  </div>
                  <span className="muted">Sem escolher preços nesta fase</span>
                </div>

                <div className="engine-field-grid">
                  {selected.fields.map((field) => (
                    <label key={field.key}>
                      <span>
                        {field.label}{field.unit ? " (" + field.unit + ")" : ""}
                        {field.required ? " *" : ""}
                      </span>
                      <FieldInput
                        field={field}
                        value={values[field.key] ?? ""}
                        onChange={(value) => {
                          setValues((current) => ({ ...current, [field.key]: value }));
                          if (field.key === "reinforcement_kg_m3") setSelectedYield(null);
                        }}
                      />
                      {field.help ? <small className="muted">{field.help}</small> : null}
                    </label>
                  ))}
                </div>
              </section>

              {selectedYield ? <div className="alert alert-success">
                Consumo selecionado: {selectedYield.quantity.toLocaleString("pt-PT")} kg/m³
                {" · " + selectedYield.source_file.split("/").pop() + " · " + selectedYield.sheet_name + " · " + selectedYield.cell_ref}
                <p>Aplicado neste cálculo, sem alterar a composição da biblioteca. Use «Guardar rascunho incompleto» para conservar esta escolha na proposta.</p>
              </div> : null}
              <details><summary>Consultar consumos históricos (opcional)</summary>
              <HistoricalYieldReferences
                key={selected.code}
                workCode={selected.code}
                canUse={canUseHistoricalYield}
                onReview={onReviewYields}
                onSelect={(selection) => {
                  setValues((current) => ({ ...current, reinforcement_kg_m3: String(selection.quantity) }));
                  setSelectedYield(selection);
                }}
              />

              </details>

              {resourcePriceError ? (
                <div className="alert alert-error">
                  Não foi possível carregar os preços dos recursos: {resourcePriceError}
                </div>
              ) : null}

              <section className="card engine-composition">
                <div className="engine-section-head">
                  <div>
                    <p className="eyebrow">2. Composição</p>
                    <h3>Recursos que irão formar o PU seco</h3>
                  </div>
                  <span className="engine-badge">Sem preços inventados</span>
                </div>

                <div className="resource-group-grid">
                  {reinforcedConcreteResourceGroups.map((group) => (
                    <article key={group.key}>
                      <strong>{group.label}</strong>
                      <p>{group.examples.join(" · ")}</p>
                    </article>
                  ))}
                </div>

                <div className="derived-table">
                  <div className="derived-row derived-head">
                    <span>Recurso / grupo</span>
                    <span>Rendimento</span>
                    <span>Base</span>
                    <span>Origem / preço</span>
                    <span>Parcial</span>
                  </div>
                  {pricedLines.map((line) => (
                    <div className="derived-row" key={line.key}>
                      <div>
                        <strong>{line.label}</strong>
                        <small>{line.note}</small>
                      </div>
                      <span>
                        {line.quantity === null
                          ? "Por definir"
                          : line.quantity.toLocaleString("pt-PT", {
                              maximumFractionDigits: 3,
                            }) + " " + line.unit}
                      </span>
                      <span className={"basis-pill " + line.basis}>
                        {line.basis === "parameter"
                          ? "Parâmetro"
                          : line.basis === "derived"
                            ? "Calculado"
                            : "Pendente"}
                      </span>
                      <span>
                        {line.resourceCode ? (
                          line.unitPrice === null ? (
                            <span className="price-missing">{line.compatibleUnit ? "Sem preço" : "Unidade incompatível"}</span>
                          ) : (
                            <span title={line.priceRow?.counterparty ?? undefined}>
                              {line.priceOrigin ? <small className={"origin-pill " + (line.priceOrigin === "l37_validated" ? "origin-validated" : "origin-missing")}>{priceOriginLabel(line.priceOrigin)}</small> : null}
                              {formatMoney(line.unitPrice)}
                            </span>
                          )
                        ) : (
                          "—"
                        )}
                      </span>
                      <strong>{formatMoney(line.partial)}</strong>
                    </div>
                  ))}
                </div>

                <div className="engine-formula">
                  <div>
                    <span>Rendimento efetivo</span>
                    <strong>×</strong>
                    <span>Preço da origem resolvida</span>
                  </div>
                  <b>= Parcial</b>
                </div>
                <p className="muted">Os grupos acima usam apenas o preço L37 validado. Não somam fallback CYPE nem substituem a decomposição da ficha piloto.</p>
              </section>

              {articleBase ? <PilotPricePanel
                sourceCode={articleBase.metadata.source_code}
                referenceLines={articleBase.metadata.reference_lines ?? []}
                parameters={values}
                cypeOriginalUnitPrice={articleBase.reference_unit_price}
                onResolved={setPilotResolution}
              /> : null}

              <SaveParametricDraft target={target} canSave={canUseHistoricalYield} pricesReady={pricesReady} onReturn={onReturn}
                snapshot={{ kind: "parametric_draft_v1", engine_version: "reinforced_concrete_v1",
                  incomplete: true, article_base: articleBase,
                  draft: { workCode: selectedCode, values, historicalYield: selectedYield, articleBase },
                  work_name: selected.name, work_unit: selected.unit,
                  parameter_labels: Object.fromEntries(selected.fields.map((field) => [field.key, field.label])),
                  subtotal: pilotResolution?.dryUnitPrice ?? calculableSubtotal,
                  price_resolution: pilotResolution ? priceResolutionSnapshot(pilotResolution) : null,
                  lines: pricedLines, missing_price_count: missingPriceCount,
                  pending_quantity_count: pricedLines.filter((line) => line.quantity === null).length }} />

              <section className="engine-bottom-grid">
                <article className="card engine-result">
                  <p className="eyebrow">3. PU seco</p>
                  <h3>
                    {pilotResolution
                      ? "PU seco da hierarquia"
                      : missingPriceCount === 0 && pricedLineCount > 0
                        ? "Subtotal técnico calculável"
                        : "Composição ainda incompleta"}
                  </h3>
                  <strong>
                    {formatMoney(pilotResolution ? pilotResolution.dryUnitPrice : calculableSubtotal)}
                  </strong>
                  <span>
                    {pilotResolution
                      ? "Soma só das linhas com preço. Recursos sem preço ficam de fora e o complemento percentual CYPE não entra. "
                        + pilotResolution.missingResourceCodes.length
                        + " recurso(s) sem preço."
                      : missingPriceCount > 0
                        ? missingPriceCount +
                          " recurso(s) com quantidade calculável ainda sem preço L37. " +
                          "Mão de obra e equipamentos continuam pendentes até validarmos os rendimentos."
                        : "Este subtotal inclui apenas linhas com rendimento e preço já disponíveis. " +
                          "Só será PU seco final quando toda a composição estiver aprovada."}
                  </span>
                </article>

                <article className="card engine-history-placeholder">
                  <p className="eyebrow">4. Validação L37</p>
                  <h3>Histórico como controlo</h3>
                  <p className="muted">
                    Depois do cálculo, comparamos com obras semelhantes para
                    detetar desvios — sem substituir automaticamente o preço.
                  </p>
                  <div className="history-placeholder-row">
                    <span>Faixa histórica</span>
                    <strong>—</strong>
                  </div>
                  <div className="history-placeholder-row">
                    <span>Últimas referências</span>
                    <strong>—</strong>
                  </div>
                </article>
              </section>
            </>
          ) : null}
        </main>
      </div>
    </section>
  );
}
